// public/js/modules/map/simplify.js
// Ramer-Douglas-Peucker path simplification + turn/stop keypoint detection.
// Client-side counterpart of utils/simplify.mjs — same algorithm, same
// {lat, lon, timestamp} interface, no external dependencies.

// Default tuning values (mirrors config.historySimplify on the server).
export const SIMPLIFICATION = {
  TOLERANCE_METERS: 10,        // RDP perpendicular-distance threshold in metres
  TURN_THRESHOLD_DEG: 32,      // heading change that forces a point to be kept
  STOP_THRESHOLD_MS: 5 * 60 * 1000, // time gap that marks a stop
  RECENT_PATH_HOURS: 24,       // how far back the "recent" (bright) path reaches
  // Stationary collapse: consecutive fixes this close together, outside a
  // trip, are ONE dwell. 30 m is above the jitter a parked tracker produces
  // (measured: 8-16 m mean, 47 m worst, on a GRIFFIN-AIR) and below anything
  // that counts as movement for ground equipment.
  DWELL_RADIUS_M: 30,
  DWELL_MIN_POINTS: 2,
  // A fix the device itself rates worse than this (posAcc, metres) carries
  // no positional information: it can neither end a dwell
  // nor move its centroid. Measured on a parked engine stand: such fixes land
  // 50-360 m away with posAcc 129-255 while the good ones sit within 15 m.
  DWELL_WEAK_FIX_M: 100,
};

function haversineM(a, b) {
  const R = 6371000;
  const la1 = parseFloat(a.lat) * Math.PI / 180, la2 = parseFloat(b.lat) * Math.PI / 180;
  const dLa = la2 - la1, dLo = (parseFloat(b.lon) - parseFloat(a.lon)) * Math.PI / 180;
  const x = Math.sin(dLa / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLo / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

/**
 * Collapse stationary runs into single dwell points.
 *
 * Why this exists: a parked tracker heartbeats every hour with a fresh GPS fix,
 * and each fix lands a few metres from the last. The turn and stop rules in
 * simplifyRoute then keep EVERY one of them - random jitter reads as a turn,
 * and every hourly gap reads as a stop - so a tracker that has not moved a
 * metre draws twenty-five scribble points a day. The customer-visible symptom
 * was "many pings on one position" (feedback 75c7c59c).
 *
 * A run is consecutive points, each within DWELL_RADIUS_M of the run's running
 * centroid, none of them in a trip. Trip start/end records always break a run
 * so their markers stay exactly where the device put them. A run becomes one
 * point at the centroid, carrying the FIRST point's fields (timestamp, reason,
 * inTrip) plus `dwell: { from, to, count }` for the marker.
 */
export function collapseDwells(points, opts = {}) {
  if (!Array.isArray(points) || points.length < 2) return points;
  const radius = opts.radiusM ?? SIMPLIFICATION.DWELL_RADIUS_M;
  const minPts = opts.minPoints ?? SIMPLIFICATION.DWELL_MIN_POINTS;
  const weakM = opts.weakFixM ?? SIMPLIFICATION.DWELL_WEAK_FIX_M;
  const isTripEvent = (p) => p.recordReason === 1 || p.recordReason === 2;
  const moving = (p) => p.inTrip === true;
  const hasFix = (p) => isFinite(parseFloat(p.lat)) && isFinite(parseFloat(p.lon));
  // posAcc only: fixOk=false is the NORMAL heartbeat state on a GRIFFIN-AIR
  // (cloud-solved position, no on-device fix) and says nothing about quality.
  const weakFix = (p) => (Number(p.posAcc) || 0) >= weakM;

  // Pass 1 - segment into runs. A run is { pts, lat, lon (centroid sums),
  // fixed } where `fixed` marks a point that must stay exactly as it is (a
  // trip event, an in-trip record, a point with no fix).
  const runs = [];
  let run = null;
  // n counts the GOOD fixes behind the centroid sums; weak fixes ride in pts only.
  const centroid = (r) => ({ lat: r.sLat / r.n, lon: r.sLon / r.n });
  const open = (p) => {
    run = weakFix(p)
      ? { pts: [p], sLat: 0, sLon: 0, n: 0, fixed: false }
      : { pts: [p], sLat: parseFloat(p.lat), sLon: parseFloat(p.lon), n: 1, fixed: false };
  };
  const close = () => { if (run) runs.push(run); run = null; };

  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    if (!hasFix(p) || isTripEvent(p) || moving(p)) { close(); runs.push({ pts: [p], fixed: true }); continue; }
    if (run) {
      // A weak fix rides along in the run without touching the centroid: the
      // device said it does not know where it is, so it cannot have moved.
      if (weakFix(p)) { run.pts.push(p); continue; }
      // A run opened by a weak fix has no centroid yet; the first good fix sets it.
      if (run.n === 0) { run.pts.push(p); run.sLat = parseFloat(p.lat); run.sLon = parseFloat(p.lon); run.n = 1; continue; }
      let inside = haversineM(centroid(run), p) <= radius;
      // One stray fix must not end a parking spot: if the next GOOD point is
      // back inside the radius the receiver wandered, the device did not.
      if (!inside) {
        let j = i + 1;
        while (j < points.length && hasFix(points[j]) && weakFix(points[j]) && !isTripEvent(points[j]) && !moving(points[j])) j++;
        const n = points[j];
        if (n && hasFix(n) && !isTripEvent(n) && !moving(n) && haversineM(centroid(run), n) <= radius) inside = true;
      }
      if (inside) { run.pts.push(p); run.sLat += parseFloat(p.lat); run.sLon += parseFloat(p.lon); run.n++; continue; }
      close();
    }
    open(p);
  }
  close();

  // Pass 2 - re-join. Two or more strays in a row split a run in pass 1, and
  // the device then went on reporting from the same spot. If the NEXT run's
  // centroid is within the radius of this one and nothing fixed sits between
  // them, they are the same parking spot: merge, strays included. (On a real
  // engine stand in Hamburg this turned 124 dwells into a handful.)
  const merged = [];
  for (const r of runs) {
    const prev = merged[merged.length - 1];
    // A run with no good fix at all (all weak) has no centroid: fold it into
    // its neighbour unconditionally - it cannot testify to any movement.
    if (prev && !prev.fixed && !r.fixed && (r.n === 0 || prev.n === 0 || haversineM(centroid(prev), centroid(r)) <= radius)) {
      prev.pts.push(...r.pts); prev.sLat += r.sLat; prev.sLon += r.sLon; prev.n += r.n;
      continue;
    }
    merged.push(r);
  }

  // Pass 3 - materialise: a run of minPts or more becomes one dwell point at
  // the centroid carrying the FIRST point's fields plus dwell {from, to, count}.
  const out = [];
  for (const r of merged) {
    if (r.fixed || r.pts.length < minPts || r.n === 0) { out.push(...r.pts); continue; }
    const first = r.pts[0], last = r.pts[r.pts.length - 1];
    const c = centroid(r);
    out.push({ ...first, lat: c.lat, lon: c.lon, dwell: { from: first.timestamp, to: last.timestamp, count: r.pts.length } });
  }
  return out;
}

function perpendicularDistMeters(p, a, b) {
  const cosLat = Math.cos(a.lat * Math.PI / 180);
  const M = 111320;
  const px = (p.lon - a.lon) * cosLat * M;
  const py = (p.lat - a.lat) * M;
  const bx = (b.lon - a.lon) * cosLat * M;
  const by = (b.lat - a.lat) * M;
  const len2 = bx * bx + by * by;
  if (len2 === 0) return Math.hypot(px, py);
  const t = Math.max(0, Math.min(1, (px * bx + py * by) / len2));
  return Math.hypot(px - t * bx, py - t * by);
}

function bearingDeg(a, b) {
  const φ1 = a.lat * Math.PI / 180;
  const φ2 = b.lat * Math.PI / 180;
  const Δλ = (b.lon - a.lon) * Math.PI / 180;
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
}

function angleDiff(a, b) {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
}

function rdpMark(points, lo, hi, tol, keep) {
  if (hi <= lo + 1) return;
  let maxDist = 0, maxIdx = lo;
  for (let i = lo + 1; i < hi; i++) {
    const d = perpendicularDistMeters(points[i], points[lo], points[hi]);
    if (d > maxDist) { maxDist = d; maxIdx = i; }
  }
  if (maxDist > tol) {
    keep[maxIdx] = true;
    rdpMark(points, lo, maxIdx, tol, keep);
    rdpMark(points, maxIdx, hi, tol, keep);
  }
}

/**
 * Simplify a GPS route using RDP + turn/stop keypoint preservation.
 * @param {Array<{lat, lon, timestamp?}>} points  Sorted chronologically.
 * @param {object} [opts]
 * @returns {Array} Simplified array (same object references)
 */
export function simplifyRoute(points, opts = {}) {
  // Stationary runs first: after this, the turn and stop rules below only ever
  // see one point per parking spot, which is what they were written for.
  points = collapseDwells(points, opts);
  if (!Array.isArray(points) || points.length <= 3) return points;

  const tol     = opts.toleranceMeters  ?? SIMPLIFICATION.TOLERANCE_METERS;
  const turnDeg = opts.turnThresholdDeg ?? SIMPLIFICATION.TURN_THRESHOLD_DEG;
  const stopMs  = opts.stopThresholdMs  ?? SIMPLIFICATION.STOP_THRESHOLD_MS;

  const keep = new Array(points.length).fill(false);
  keep[0] = true;
  keep[points.length - 1] = true;

  rdpMark(points, 0, points.length - 1, tol, keep);

  for (let i = 1; i < points.length - 1; i++) {
    // Always keep explicit trip start/end events and in-trip transitions so
    // they can be highlighted on the route (never simplified away).
    const reason = points[i].recordReason;
    if (reason === 1 || reason === 2) keep[i] = true;
    if (points[i].inTrip != null && points[i - 1].inTrip != null
        && points[i].inTrip !== points[i - 1].inTrip) {
      keep[i] = true;
      keep[i - 1] = true;
    }
    if (turnDeg > 0) {
      const b1 = bearingDeg(points[i - 1], points[i]);
      const b2 = bearingDeg(points[i],     points[i + 1]);
      if (angleDiff(b1, b2) >= turnDeg) keep[i] = true;
    }
    if (stopMs > 0 && points[i].timestamp && points[i + 1]?.timestamp) {
      const dt = new Date(points[i + 1].timestamp) - new Date(points[i].timestamp);
      if (dt >= stopMs) { keep[i] = true; keep[i + 1] = true; }
    }
  }

  return points.filter((_, i) => keep[i]);
}

/**
 * Indices of points immediately before a stop (a time gap >= threshold).
 * Used to drop "stop" markers along a route.
 * @param {Array<{timestamp?}>} points
 * @param {number} [stopThresholdMs]
 * @returns {number[]}
 */
export function findStopIndices(points, stopThresholdMs = SIMPLIFICATION.STOP_THRESHOLD_MS) {
  const stops = [];
  if (!Array.isArray(points)) return stops;
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i];
    const b = points[i + 1];
    if (a?.timestamp && b?.timestamp) {
      const dt = new Date(b.timestamp) - new Date(a.timestamp);
      if (dt >= stopThresholdMs) stops.push(i);
    }
  }
  return stops;
}
